<template>
  <!-- การ์ด "App Knowledge Center" ในหน้า Core Configurations (/setup) — มีปุ่มบันทึก/ทดสอบของตัวเอง ไม่ผูกกับ Save รวม
       ค่าทั้งหมดอยู่ที่ sy.Dbsettings (key ขึ้นต้น KnowledgeCenterClient.) ซึ่ง server กรองออกจาก Setup/OverviewGeneral แล้ว
       ⇒ การ์ดอื่นในหน้านี้ไม่เห็นและไม่ทับค่าของการ์ดนี้ · key 2 ใบเป็น write-only: server คืนแค่ hasAdminKey/hasRuntimeKey -->
  <v-card v-if="initialized && !forbidden" class="config-card kcc-settings-card" elevation="2">
    <v-card-title class="card-header">
      <v-icon color="white" class="mr-3">mdi-book-lock-outline</v-icon>
      <span>{{ $t('kccCardTitle') }}</span>
    </v-card-title>
    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <v-card-text class="card-content">
      <div class="section-title">{{ $t('kccCardSubtitle') }}</div>

      <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mb-4" icon="mdi-alert-circle-outline">
        <span class="text-body-2">{{ loadError }}</span>
      </v-alert>
      <v-alert v-if="view && view.warning" type="warning" variant="tonal" density="compact" class="mb-4" icon="mdi-alert-outline">
        <span class="text-body-2">{{ view.warning }}</span>
      </v-alert>

      <v-form ref="formRef" @submit.prevent="save">
        <div class="setting-item mb-4">
          <v-switch v-model="form.enabled" color="primary" inset hide-details :disabled="fieldsDisabled">
            <template v-slot:label>
              <div class="switch-label">
                <v-icon size="small" class="mr-2">mdi-power</v-icon>
                <span>{{ $t('kccEnabled') }}</span>
              </div>
            </template>
          </v-switch>
        </div>

        <v-row>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="form.baseUrl"
              :label="$t('kccBaseUrl')"
              :hint="$t('kccBaseUrlHint')"
              persistent-hint
              placeholder="https://"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-web"
              autocomplete="off"
              :rules="[baseUrlRule]"
              :disabled="fieldsDisabled"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              :model-value="form.channel"
              :label="$t('kccChannel')"
              :hint="$t('kccChannelHint')"
              persistent-hint
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-broadcast"
              autocomplete="off"
              :rules="[channelRule]"
              :disabled="fieldsDisabled"
              @update:model-value="onChannelInput"
            />
          </v-col>
        </v-row>

        <!-- key 2 ใบ: ช่องว่าง = คงค่าเดิม · ล้าง key = ตั้งธง clear* แล้วมีผลเมื่อกดบันทึกของการ์ดนี้ -->
        <v-row class="mt-2">
          <v-col v-for="k in keyFields" :key="k.kind" cols="12" md="6">
            <div class="setting-item">
              <div class="d-flex align-center flex-wrap ga-2 mb-3">
                <v-icon size="small">{{ k.icon }}</v-icon>
                <span class="switch-label">{{ k.title }}</span>
                <v-chip
                  v-if="k.clearing"
                  size="small"
                  variant="tonal"
                  color="warning"
                  prepend-icon="mdi-key-remove"
                  :closable="!fieldsDisabled"
                  @click:close="undoClear(k.kind)"
                >
                  {{ $t('kccKeyWillClear') }}
                </v-chip>
                <v-chip v-else-if="k.hasKey" size="small" variant="tonal" color="success" prepend-icon="mdi-check-circle-outline">
                  {{ $t('kccKeySaved') }}
                </v-chip>
                <v-chip v-else size="small" variant="tonal" prepend-icon="mdi-key-outline">
                  {{ $t('kccKeyNotSaved') }}
                </v-chip>
                <v-spacer />
                <v-btn
                  v-if="k.hasKey && !k.clearing && !viewOnly"
                  size="small"
                  variant="text"
                  color="error"
                  prepend-icon="mdi-key-remove"
                  :disabled="fieldsDisabled"
                  @click="askClear(k.kind)"
                >
                  {{ $t('kccClearKey') }}
                </v-btn>
              </div>
              <v-text-field
                v-model="form[k.model]"
                :label="k.label"
                :hint="k.hasKey ? $t('kccKeyKeepHint') : ''"
                :persistent-hint="k.hasKey"
                type="password"
                autocomplete="new-password"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-key-variant"
                :disabled="fieldsDisabled || k.clearing"
              />
            </div>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.timeoutSeconds"
              :label="$t('kccTimeoutSeconds')"
              hint="1–3600"
              persistent-hint
              type="number"
              min="1"
              max="3600"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-timer-outline"
              :rules="[timeoutRule]"
              :disabled="fieldsDisabled"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.uploadTimeoutSeconds"
              :label="$t('kccUploadTimeoutSeconds')"
              hint="1–3600"
              persistent-hint
              type="number"
              min="1"
              max="3600"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-cloud-upload-outline"
              :rules="[timeoutRule]"
              :disabled="fieldsDisabled"
            />
          </v-col>
        </v-row>
      </v-form>

      <v-alert v-if="saveError" type="error" variant="tonal" density="compact" class="mt-4" icon="mdi-alert-circle-outline">
        <span class="text-body-2">{{ saveError }}</span>
      </v-alert>

      <div class="d-flex flex-wrap align-center ga-3 mt-6">
        <div v-if="updatedText" class="text-caption text-medium-emphasis d-flex align-center">
          <v-icon size="small" class="mr-1">mdi-history</v-icon>
          <span>{{ updatedText }}</span>
        </div>
        <v-spacer />
        <!-- ทดสอบด้วยค่าในฟอร์มตอนกด (ยังไม่บันทึกก็ได้) · ช่องว่าง = server ใช้ค่าที่บันทึกไว้ · ไม่เปลี่ยนค่าใด ⇒ เปิดให้ viewOnly ด้วย -->
        <v-btn
          variant="outlined"
          color="primary"
          prepend-icon="mdi-lan-connect"
          :loading="testing"
          :disabled="saving || loading"
          @click="test"
        >
          {{ $t('kccTest') }}
        </v-btn>
        <v-btn
          v-if="!viewOnly"
          color="primary"
          variant="elevated"
          prepend-icon="mdi-content-save"
          :loading="saving"
          :disabled="!view || testing || loading"
          @click="save"
        >
          {{ $t('kccSave') }}
        </v-btn>
      </div>

      <v-alert v-if="testError" type="error" variant="tonal" density="compact" class="mt-4" icon="mdi-alert-circle-outline">
        <span class="text-body-2">{{ testError }}</span>
      </v-alert>

      <div v-if="testRows.length" class="kcc-test-results mt-4">
        <div v-for="row in testRows" :key="row.kind" class="kcc-test-row">
          <v-icon :color="row.color" class="mr-3">{{ row.icon }}</v-icon>
          <div class="flex-grow-1">
            <div class="d-flex align-center flex-wrap ga-2">
              <span class="font-weight-medium">{{ row.label }}</span>
              <span :class="row.textClass">{{ row.statusText }}</span>
              <v-chip v-if="row.httpStatus !== null" size="x-small" variant="tonal">HTTP {{ row.httpStatus }}</v-chip>
              <v-chip v-if="row.reason" size="x-small" variant="tonal" :color="row.ok ? undefined : 'error'">{{ row.reason }}</v-chip>
              <v-chip v-if="row.count !== null" size="x-small" variant="tonal" color="primary">
                {{ $t('kccTestCount', { count: row.count }) }}
              </v-chip>
            </div>
            <div v-if="row.message" class="text-caption text-medium-emphasis mt-1">{{ row.message }}</div>
          </div>
        </div>
      </div>
    </v-card-text>

    <!-- ยืนยันล้าง key — ล้างจริงเมื่อกดบันทึกของการ์ด (ระหว่างนั้น server ยังใช้ key เดิม) -->
    <v-dialog v-model="clearDialog.open" max-width="560px">
      <v-card>
        <v-card-title class="bg-error text-white d-flex align-center">
          <v-icon class="mr-2">mdi-key-remove</v-icon>
          <span>{{ $t('kccClearKey') }} — {{ clearDialogLabel }}</span>
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="text-body-1">{{ $t('kccConfirmClearKey', { name: clearDialogLabel }) }}</div>
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer />
          <v-btn variant="outlined" prepend-icon="mdi-close" @click="clearDialog.open = false">{{ $t('kccCancel') }}</v-btn>
          <v-btn color="error" variant="elevated" prepend-icon="mdi-key-remove" @click="confirmClear">{{ $t('kccConfirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="savedSnack" color="success" :timeout="3000" location="top">
      <v-icon class="mr-2">mdi-check-circle</v-icon>{{ $t('kccSaved') }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { kccApi, kccErrorKey } from '@/services/kccApi';

/**
 * การ์ดค่าตั้ง App Knowledge Center ของหน้า Core Configurations (TASK genie-kmc-connect T8)
 *
 * - โหลด `GET settings` ตอน mounted → **401 = role นี้ไม่มีสิทธิ์** (APIAuthen ให้แค่ role 0,1) ⇒ emit `forbidden`
 *   แล้วซ่อนตัวเอง · ยังไม่โหลดเสร็จ = ยังไม่ render (กันการ์ดแวบให้ role 2 เห็น)
 * - key 2 ใบเป็น write-only: ช่องว่าง = คงค่าเดิม · "ล้าง key" (ต้องยืนยัน) ⇒ ส่ง `clearAdminKey/clearRuntimeKey`
 *   ตอนกดบันทึก · บันทึกสำเร็จ ⇒ ใช้ view ที่ server คืนมา แล้วล้างช่อง key
 * - ทุก error แสดงแบบ `v-alert` ในการ์ด (ไม่ใช้ Swal, ไม่ใช้ `$call` — ดูหัวไฟล์ `services/kccApi.js`)
 */
const DEFAULT_CHANNEL = 'LHBYOU_APP';
const DEFAULT_TIMEOUT_SECONDS = 10;
const DEFAULT_UPLOAD_TIMEOUT_SECONDS = 600;
const CHANNEL_PATTERN = /^[A-Z0-9_]{1,50}$/;

function emptyForm() {
  return {
    enabled: false,
    baseUrl: '',
    adminKey: '',
    runtimeKey: '',
    clearAdminKey: false,
    clearRuntimeKey: false,
    channel: DEFAULT_CHANNEL,
    timeoutSeconds: DEFAULT_TIMEOUT_SECONDS,
    uploadTimeoutSeconds: DEFAULT_UPLOAD_TIMEOUT_SECONDS,
  };
}

function trimOrNull(value) {
  const text = value === undefined || value === null ? '' : String(value).trim();
  return text === '' ? null : text;
}

function toIntOrNull(value) {
  const text = trimOrNull(value);
  if (text === null) return null;
  const n = Number(text);
  return Number.isInteger(n) ? n : null;
}

export default {
  name: 'KccSettingsCard',
  props: {
    /** สิทธิ์ดูอย่างเดียวของหน้า Setup — ปิดทุกช่อง + ซ่อนปุ่มบันทึก/ล้าง key (ปุ่มทดสอบยังกดได้ เพราะไม่เปลี่ยนค่าใด) */
    viewOnly: { type: Boolean, default: false },
  },
  emits: ['forbidden'],
  data() {
    return {
      initialized: false,
      forbidden: false,
      loading: false,
      saving: false,
      testing: false,
      view: null,
      form: emptyForm(),
      loadError: '',
      saveError: '',
      testError: '',
      testResult: null,
      clearDialog: { open: false, kind: '' },
      savedSnack: false,
    };
  },
  computed: {
    fieldsDisabled() {
      return this.viewOnly || this.saving || this.loading || !this.view;
    },
    keyFields() {
      const view = this.view || {};
      return [
        {
          kind: 'admin',
          model: 'adminKey',
          title: this.$t('kccTestAdmin'),
          label: this.$t('kccAdminKey'),
          icon: 'mdi-shield-key-outline',
          hasKey: !!view.hasAdminKey,
          clearing: !!this.form.clearAdminKey,
        },
        {
          kind: 'runtime',
          model: 'runtimeKey',
          title: this.$t('kccTestRuntime'),
          label: this.$t('kccRuntimeKey'),
          icon: 'mdi-message-text-lock-outline',
          hasKey: !!view.hasRuntimeKey,
          clearing: !!this.form.clearRuntimeKey,
        },
      ];
    },
    clearDialogLabel() {
      return this.clearDialog.kind === 'runtime' ? this.$t('kccRuntimeKey') : this.$t('kccAdminKey');
    },
    updatedText() {
      const view = this.view;
      if (!view || !view.updateDate) return '';
      // wire datetime ไม่มี Z ⇒ แสดงตามตัวอักษร ไม่แปลงโซนเวลา
      const when = String(view.updateDate).replace('T', ' ').slice(0, 16);
      return view.updateBy ? `${when} · ${view.updateBy}` : when;
    },
    testRows() {
      const result = this.testResult;
      if (!result) return [];
      return [
        this.toTestRow('admin', this.$t('kccTestAdmin'), result.admin),
        this.toTestRow('runtime', this.$t('kccTestRuntime'), result.runtime),
      ];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        this.applyView(await kccApi.settings());
      } catch (err) {
        if (err && err.httpStatus === 401) {
          this.forbidden = true;
          this.$emit('forbidden');
          return;
        }
        this.loadError = this.errorText(err);
      } finally {
        this.loading = false;
        this.initialized = true;
      }
    },
    applyView(view) {
      this.view = view || null;
      const form = emptyForm();
      if (view) {
        form.enabled = !!view.enabled;
        form.baseUrl = view.baseUrl || '';
        form.channel = view.channel || DEFAULT_CHANNEL;
        form.timeoutSeconds = view.timeoutSeconds || DEFAULT_TIMEOUT_SECONDS;
        form.uploadTimeoutSeconds = view.uploadTimeoutSeconds || DEFAULT_UPLOAD_TIMEOUT_SECONDS;
      }
      this.form = form;
    },
    async save() {
      if (this.viewOnly || !this.view) return;
      const { valid } = await this.$refs.formRef.validate();
      if (!valid) return;
      this.saving = true;
      this.saveError = '';
      const form = this.form;
      try {
        const saved = await kccApi.saveSettings({
          enabled: !!form.enabled,
          baseUrl: trimOrNull(form.baseUrl) || '',
          adminKey: form.clearAdminKey ? null : trimOrNull(form.adminKey),
          runtimeKey: form.clearRuntimeKey ? null : trimOrNull(form.runtimeKey),
          clearAdminKey: !!form.clearAdminKey,
          clearRuntimeKey: !!form.clearRuntimeKey,
          channel: (trimOrNull(form.channel) || DEFAULT_CHANNEL).toUpperCase(),
          timeoutSeconds: toIntOrNull(form.timeoutSeconds),
          uploadTimeoutSeconds: toIntOrNull(form.uploadTimeoutSeconds),
        });
        if (saved) {
          this.applyView(saved);
        } else {
          await this.load();
        }
        this.savedSnack = true;
      } catch (err) {
        this.saveError = this.errorText(err);
      } finally {
        this.saving = false;
      }
    },
    async test() {
      this.testing = true;
      this.testError = '';
      this.testResult = null;
      const form = this.form;
      try {
        this.testResult = await kccApi.testConnections({
          baseUrl: trimOrNull(form.baseUrl),
          adminKey: trimOrNull(form.adminKey),
          runtimeKey: trimOrNull(form.runtimeKey),
        });
      } catch (err) {
        this.testError = this.errorText(err);
      } finally {
        this.testing = false;
      }
    },
    toTestRow(kind, label, probe) {
      const p = probe || { skipped: true };
      const state = p.skipped ? 'skipped' : p.ok ? 'ok' : 'fail';
      const looks = {
        ok: { icon: 'mdi-check-circle', color: 'success', text: 'kccTestOk' },
        fail: { icon: 'mdi-close-circle', color: 'error', text: 'kccTestFail' },
        skipped: { icon: 'mdi-minus-circle-outline', color: 'secondary', text: 'kccTestSkipped' },
      };
      const look = looks[state];
      return {
        kind,
        label,
        ok: state === 'ok',
        icon: look.icon,
        color: look.color,
        textClass: `text-${look.color}`,
        statusText: this.$t(look.text),
        httpStatus: p.httpStatus === undefined || p.httpStatus === null ? null : p.httpStatus,
        reason: p.reason || '',
        message: p.message || '',
        count: p.count === undefined || p.count === null ? null : p.count,
      };
    },
    askClear(kind) {
      this.clearDialog = { open: true, kind };
    },
    confirmClear() {
      if (this.clearDialog.kind === 'runtime') {
        this.form.clearRuntimeKey = true;
        this.form.runtimeKey = '';
      } else {
        this.form.clearAdminKey = true;
        this.form.adminKey = '';
      }
      this.clearDialog = { open: false, kind: this.clearDialog.kind };
    },
    undoClear(kind) {
      if (kind === 'runtime') {
        this.form.clearRuntimeKey = false;
      } else {
        this.form.clearAdminKey = false;
      }
    },
    onChannelInput(value) {
      this.form.channel = String(value || '').toUpperCase();
    },
    baseUrlRule(value) {
      const text = String(value || '').trim();
      if (!text) return !this.form.enabled || this.$t('kccBaseUrlHint');
      return /^https?:\/\/\S+$/i.test(text) || this.$t('kccBaseUrlHint');
    },
    channelRule(value) {
      return CHANNEL_PATTERN.test(String(value || '').trim()) || this.$t('kccChannelHint');
    },
    timeoutRule(value) {
      const n = Number(value);
      return (Number.isInteger(n) && n >= 1 && n <= 3600) || '1–3600';
    },
    /** 400 = server validate ไม่ผ่าน ⇒ ข้อความของ server ตรงที่สุด · 412/424/502 ⇒ ข้อความกลางของ kcc + reason ของ Center */
    errorText(err) {
      const key = kccErrorKey(err);
      const message = err && err.message;
      const base = key === 'kccCenterError' && message ? message : this.$t(key);
      const reason = err && err.data && err.data.reason;
      return reason ? `${base} (${reason})` : base;
    },
  },
};
</script>

<style scoped>
/* โครงเดียวกับการ์ดอื่นในหน้า Setup (style ของ Setup.vue เป็น scoped ไม่ถึงข้างในคอมโพเนนต์นี้) — สีจาก theme เท่านั้น */
.kcc-settings-card {
  border-radius: 16px !important;
  width: 100%;
}

.card-header {
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 30%, rgba(var(--v-theme-primary), 0.08) 100%);
  border-radius: 16px 16px 0 0 !important;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.card-content {
  padding: 24px;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  flex: 0 0 4px;
  width: 4px;
  height: 20px;
  background: rgb(var(--v-theme-primary));
  margin-right: 12px;
  border-radius: 2px;
}

.setting-item {
  background: rgba(var(--v-theme-on-surface), 0.03);
  border-radius: 12px;
  padding: 16px;
}

.switch-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

.kcc-test-results {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
}

.kcc-test-row {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
}

.kcc-test-row + .kcc-test-row {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

@media (max-width: 600px) {
  .card-content {
    padding: 16px;
  }
}
</style>
